/**
 * availabilityModel — pure helpers for the teacher Availability surface
 * (BON-241). Kept out of the component so the draft logic, the summary
 * sentence, and the resolver are unit-testable on their own.
 */
import type { AvailabilitySlot, ExceptionEntry, RhythmEntry } from '../../../api/availability';

/** Row order in the design: Sunday first. Index IS the stored weekday
 *  (0=Sunday … 6=Saturday, matching JS `Date.getUTCDay()`). */
export const WEEKDAYS: ReadonlyArray<{ weekday: number; label: string; short: string }> = [
  { weekday: 0, label: 'Sunday', short: 'Sun' },
  { weekday: 1, label: 'Monday', short: 'Mon' },
  { weekday: 2, label: 'Tuesday', short: 'Tue' },
  { weekday: 3, label: 'Wednesday', short: 'Wed' },
  { weekday: 4, label: 'Thursday', short: 'Thu' },
  { weekday: 5, label: 'Friday', short: 'Fri' },
  { weekday: 6, label: 'Saturday', short: 'Sat' },
];

/** Local draft: weekday → slot. A weekday absent from the map is "not set". */
export type RhythmDraft = Partial<Record<number, AvailabilitySlot>>;

export function rhythmToDraft(rhythm: RhythmEntry[]): RhythmDraft {
  const draft: RhythmDraft = {};
  for (const entry of rhythm) draft[entry.weekday] = entry.state;
  return draft;
}

export function draftToRhythm(draft: RhythmDraft): RhythmEntry[] {
  return WEEKDAYS.filter((d) => draft[d.weekday] !== undefined).map((d) => ({
    weekday: d.weekday,
    state: draft[d.weekday] as AvailabilitySlot,
  }));
}

/** Today as `YYYY-MM-DD`, read in UTC to match how the API stores dates. */
export function todayKey(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/**
 * The earliest date still editable — UTC today minus one day, mirroring
 * `editableFloorKey` in backend/src/services/staff-availability.service.ts.
 * Keep the two in sync: a stricter client floor would hide dates the server
 * still accepts, and a looser one produces a 400 on save.
 *
 * The extra day exists because UTC rolls over at ~4pm PT / 7pm ET, and a
 * teacher blocking off "tonight" at 6pm local must not be told it's past.
 */
export function editableFloorKey(now: Date = new Date()): string {
  return todayKey(new Date(now.getTime() - 86_400_000));
}

/** `YYYY-MM-DD` → "Fri Jun 26", rendered in UTC like the rest of dashboards. */
export function formatExceptionDate(dateKey: string): string {
  return new Date(`${dateKey}T00:00:00.000Z`).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The design's summary chip — "5 days a week · evenings + Saturdays". Built
 * from whatever is actually set rather than a fixed template, so a partial
 * rhythm still reads honestly. Returns null when nothing is declared.
 */
export function summarizeRhythm(draft: RhythmDraft): string | null {
  const working = WEEKDAYS.filter((d) => {
    const slot = draft[d.weekday];
    return slot !== undefined && slot !== 'off';
  });
  if (working.length === 0) {
    const anySet = WEEKDAYS.some((d) => draft[d.weekday] !== undefined);
    return anySet ? 'No days available' : null;
  }

  const dayCount = `${working.length} ${working.length === 1 ? 'day' : 'days'} a week`;
  const slots = new Set(working.map((d) => draft[d.weekday] as AvailabilitySlot));
  if (slots.size === 1) {
    const only = [...slots][0];
    const phrase = only === 'all_day' ? 'all day' : only === 'evening' ? 'evenings' : 'daytimes';
    return `${dayCount} · ${phrase}`;
  }
  return `${dayCount} · mixed hours`;
}

/**
 * Is this `YYYY-MM-DD` still editable? Mirrors the server's floor check.
 *
 * Needed as a real guard, not just the input's `min`: on a `<input
 * type="date">` `min` is advisory for keyboard-entered values, and the Add
 * button sits outside a form so constraint validation never runs. Without
 * this the row is accepted locally and the entire save then 400s.
 */
export function isEditableDate(dateKey: string, now: Date = new Date()): boolean {
  return dateKey >= editableFloorKey(now);
}

/**
 * Exceptions sorted by date, no-longer-editable ones dropped. Applied both
 * when seeding the draft AND at save time: a tab left open across the UTC
 * rollover would otherwise submit a now-past date and lose the whole save
 * to a 400.
 */
export function futureExceptions(
  exceptions: ExceptionEntry[],
  now: Date = new Date(),
): ExceptionEntry[] {
  const floor = editableFloorKey(now);
  return exceptions.filter((e) => e.date >= floor).sort((a, b) => a.date.localeCompare(b.date));
}

/** True when the draft differs from what was last saved. */
export function isDirty(
  saved: { rhythm: RhythmEntry[]; exceptions: ExceptionEntry[] },
  draft: { rhythm: RhythmDraft; exceptions: ExceptionEntry[] },
): boolean {
  const savedRhythm = JSON.stringify(
    [...saved.rhythm].sort((a, b) => a.weekday - b.weekday).map((r) => [r.weekday, r.state]),
  );
  const draftRhythm = JSON.stringify(draftToRhythm(draft.rhythm).map((r) => [r.weekday, r.state]));
  if (savedRhythm !== draftRhythm) return true;

  const key = (list: ExceptionEntry[]) =>
    JSON.stringify(
      [...list]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((e) => [e.date, e.state, e.note ?? '']),
    );
  return key(saved.exceptions) !== key(draft.exceptions);
}
